import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DialogContent, DialogActions, Button } from '@material-ui/core';
import { Formik } from 'formik';
import Modal from 'components/Shared/Modal';
import CreateUserStepIndicator from './CreateUserStepIndicator';
import Step1EmailCheck from './Step1EmailCheck';
import Step2UserDetails from './Step2UserDetails';
import Step3AccountSelection from './Step3AccountSelection';
import Step4AccountSettings from './Step4AccountSettings';
import RoundedButton from 'components/Shared/RoundedButton';
import { createUser, validateUserData, addAccountsToUser } from 'redux_store/api/endpoints/users';
import { REQUEST_STATUS, SNACKBAR_VARIANTS } from 'redux_store/constants';
import { enqueueSnackbar } from 'redux_store/slices/notificationsSlice';
import {
  setCreateUserStep,
  resetCreateUserFlow,
  resetCreateUserStatus,
  resetValidateUserStatus,
  setManagedUser,
  setUserAccounts, setSelectedAccount,
} from 'redux_store/slices/userAccountManagementSlice';
import { useModalStyles } from './styles';

const CreateUserModal = ({ open, onClose, onUserCreated, onEditUser, preselectedAccount, defaultSelectedAccount }) => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const { en: { titles, btn } } = useSelector((state) => state.i18n);
  const {
    createUserFlow,
    createUserStatus,
    validateUserStatus,
    validateUserValidationErrors,
  } = useSelector(
    (state) => state.userAccountManagement,
  );
  const { isDistributionSponsor, metadata } = useSelector((state) => state.user);

  const [backendErrors, setBackendErrors] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountSettings, setAccountSettings] = useState(null);

  // Skip account selection step for non-DS accounts OR when account is preselected (e.g. from account creation)
  const skipAccountSelection = !isDistributionSponsor || !!preselectedAccount;

  // Determine step flow based on account type
  const getTotalSteps = () => {
    return skipAccountSelection ? 3 : 4;
  };

  const getDisplayStep = (currentStep) => {
    if (!skipAccountSelection) {
      return currentStep; // DS accounts without preselection: 1, 2, 3, 4
    } else {
      // Skip step 3 (Account Selection)
      if (currentStep <= 2) return currentStep; // 1, 2
      if (currentStep === 4) return 3; // Show step 4 as step 3
      return currentStep;
    }
  };

  const getActualStep = (displayStep) => {
    if (!skipAccountSelection) {
      return displayStep; // DS accounts without preselection: no conversion needed
    } else {
      // Convert display step to actual step
      if (displayStep <= 2) return displayStep; // 1, 2
      if (displayStep === 3) return 4; // Display step 3 is actually step 4
      return displayStep;
    }
  };

  // Initialize selectedAccounts for non-DS accounts or when account is preselected
  useEffect(() => {
    if (selectedAccounts.length > 0) return;

    if (preselectedAccount) {
      setSelectedAccounts([{
        uuid: preselectedAccount.uuid,
        accountBusinessName: preselectedAccount.businessName,
        businessName: preselectedAccount.businessName,
        name: preselectedAccount.businessName,
      }]);
    } else if (isDistributionSponsor && defaultSelectedAccount) {
      // Pre-select account from URL on Step 3 for DS accounts
      setSelectedAccounts([{
        uuid: defaultSelectedAccount.uuid,
        accountBusinessName: defaultSelectedAccount.businessName,
        businessName: defaultSelectedAccount.businessName,
        name: defaultSelectedAccount.businessName,
      }]);
    } else if (!isDistributionSponsor && metadata?.currentAccount) {
      const currentAccount = {
        uuid: metadata.currentAccount.uuid,
        accountBusinessName: metadata.currentAccount.businessName || metadata.currentAccount.name,
        businessName: metadata.currentAccount.businessName || metadata.currentAccount.name,
        name: metadata.currentAccount.businessName || metadata.currentAccount.name,
      };
      setSelectedAccounts([currentAccount]);
    }
  }, [isDistributionSponsor, metadata?.currentAccount, selectedAccounts.length, preselectedAccount, defaultSelectedAccount]);


  // Auto-reset failed status after 2 seconds
  useEffect(() => {
    if (createUserStatus === REQUEST_STATUS.failed) {
      const timeout = setTimeout(() => {
        dispatch(resetCreateUserStatus());
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [createUserStatus, dispatch]);

  // Update backend errors when validation errors change
  useEffect(() => {
    if (validateUserValidationErrors) {
      setBackendErrors(validateUserValidationErrors);
    }
  }, [validateUserValidationErrors]);

  const handleClose = () => {
    dispatch(resetCreateUserFlow());
    setBackendErrors({});
    setSelectedAccounts([]);
    setAccountSettings(null);
    onClose();
  };

  const handleNextStep = async (formikValues) => {
    // If we're on Step 2, validate user data before proceeding
    if (createUserFlow.currentStep === 2) {
      await handleValidateAndProceed(formikValues);
    } else {
      // For other steps, proceed normally with conditional logic
      let nextStep = createUserFlow.currentStep + 1;

      // Skip step 3 (Account Selection) when not needed
      if (skipAccountSelection && nextStep === 3) {
        nextStep = 4; // Skip to Account Settings
      }

      dispatch(setCreateUserStep(nextStep));
    }
  };

  const handleValidateAndProceed = async (values) => {
    try {
      setBackendErrors({});
      dispatch(resetValidateUserStatus());

      // Prepare validation payload (only basic user data needed for new validation endpoint)
      const validationPayload = {
        email: createUserFlow.checkedEmail,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone?.replace(/[^\d]/g, '') || '',
      };


      const result = await dispatch(validateUserData({ formValues: validationPayload }));

      if (validateUserData.fulfilled.match(result)) {
        // Validation successful - proceed to next step
        let nextStep = createUserFlow.currentStep + 1;

        // Skip step 3 (Account Selection) when not needed
        if (skipAccountSelection && nextStep === 3) {
          nextStep = 4;
        }

        dispatch(setCreateUserStep(nextStep));
      } else if (validateUserData.rejected.match(result)) {
        // Validation failed - errors will be shown via useEffect
      }
    } catch (error) {
      // Error is handled by Redux thunk
    }
  };

  const handlePrevStep = () => {
    let prevStep = createUserFlow.currentStep - 1;

    // Handle step 4 back navigation when account selection is skipped
    if (skipAccountSelection && createUserFlow.currentStep === 4) {
      if (createUserFlow.existingUser) {
        // For existing users: go back to step 1 (email check) to avoid validation issues
        prevStep = 1;
      } else {
        // For new users: go back to step 2 (user details) as normal
        prevStep = 2;
      }
    }

    dispatch(setCreateUserStep(prevStep));
  };

  const handleCreateUser = async (values, { setSubmitting }) => {
    try {
      setBackendErrors({});

      // Transform accountSettings to the correct API format
      const accountUUIDs = accountSettings ? accountSettings.map(account => account.uuid) : [];

      // Extract role and permissions from the first account (since all accounts get the same settings)
      const firstAccount = accountSettings && accountSettings.length > 0 ? accountSettings[0] : null;

      const permissions = firstAccount?.userPermissions || {
        managesUsers: false,
        receiveClaimsEmails: false,
        administerClaims: false,
        administerClaimReports: false,
        receiveSurveysEmails: false,
        imminentRiskEmails: false,
        managesAlert: false,
      };

      // Check if this is an existing user (for non-DS accounts)
      const isExistingUser = createUserFlow.existingUser && !isDistributionSponsor;

      let result;

      if (isExistingUser) {
        // For existing users in non-DS accounts: Add user to current account
        const settings = {
          role: firstAccount?.role || 'assistant',
          providerTitle: firstAccount?.providerTitle || '',
          providerNPINumber: firstAccount?.providerNPINumber || '',
          ...permissions
        };

        result = await dispatch(addAccountsToUser({
          userUuid: createUserFlow.existingUser.uuid,
          accountIds: accountUUIDs,
          settings: settings
        }));
      } else {
        // For new users: Create user normally
        const formValues = {
          email: createUserFlow.checkedEmail,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone?.replace(/[^\d]/g, '') || '', // Clean phone number - remove formatting
          role: firstAccount?.role || 'assistant',
          providerTitle: firstAccount?.providerTitle || '',
          providerNPINumber: firstAccount?.providerNPINumber || '',
          accountUUIDs: accountUUIDs,
          permissions: permissions,
        };

        result = await dispatch(createUser({ formValues }));
      }

      if (createUser.fulfilled.match(result) || addAccountsToUser.fulfilled.match(result)) {
        let userDataForState;
        const formattedAccounts = accountSettings?.map(e => {
          return {
            ...e,
            accountBusinessName: e.accountBusinessName || e.businessName,
            accountUUID: e.uuid || e.accountUUID,
          };
        }) ?? [];

        if (isExistingUser) {
          // For existing users: use the existing user data with added accounts
          userDataForState = {
            uuid: createUserFlow.existingUser.uuid,
            firstName: createUserFlow.existingUser.firstName,
            lastName: createUserFlow.existingUser.lastName,
            email: createUserFlow.existingUser.email,
            phone: createUserFlow.existingUser.phone || '', // Add phone for existing users
            hasUserAcceptedInvitation: true,
            userAccounts: formattedAccounts,
          };
        } else {
          // For new users: use the created user data and form values
          const newUser = result.payload.createdUser;
          userDataForState = {
            uuid: newUser.uuid,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            email: newUser.email,
            phone: newUser.phone || values.phone || '', // Try API response first, then form values
            hasUserAcceptedInvitation: true,
            userAccounts: formattedAccounts,
          };
        }

        dispatch(setManagedUser(userDataForState));
        dispatch(setUserAccounts(formattedAccounts));
        if (formattedAccounts.length) {
          dispatch(setSelectedAccount(formattedAccounts[0].accountUUID));
        }

        // Show success notification for non-DS accounts
        if (!isDistributionSponsor) {
          const message = isExistingUser
            ? `User ${userDataForState.firstName} ${userDataForState.lastName} has been successfully added to your account.`
            : `User ${userDataForState.firstName} ${userDataForState.lastName} has been successfully created.`;

          dispatch(enqueueSnackbar({
            message,
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
          }));
        }

        // Call parent's callback with the user data
        if (onUserCreated) {
          onUserCreated(userDataForState);
        }

        // Close modal after a short delay for better UX
        setTimeout(() => {
          handleClose();
        }, 500);
      } else if (createUser.rejected.match(result) || addAccountsToUser.rejected.match(result)) {
        // Handle validation errors
        if (result.payload?.validationErrors) {
          setBackendErrors(result.payload.validationErrors);
        }
      }
    } catch (error) {
      // Error is handled by Redux thunk
    }
    setSubmitting(false);
  };

  const handleEditExistingUser = (existingUser) => {
    if (onEditUser) {
      handleClose();
      onEditUser(existingUser);
    }
  };

  const renderStepContent = (formikProps) => {
    if (createUserFlow.currentStep === 1) {
      return <Step1EmailCheck onEditUser={handleEditExistingUser} />;
    }

    if (createUserFlow.currentStep === 2) {
      return (
        <Step2UserDetails
          values={formikProps?.values}
          backendErrors={backendErrors}
        />
      );
    }

    if (createUserFlow.currentStep === 3) {
      return (
        <Step3AccountSelection
          selectedAccounts={selectedAccounts}
          onSelectionChange={setSelectedAccounts}
          validationErrors={backendErrors}
        />
      );
    }

    if (createUserFlow.currentStep === 4) {
      return (
        <Step4AccountSettings
          selectedAccounts={selectedAccounts}
          onSubmit={setAccountSettings}
          validationErrors={backendErrors}
          setValidationErrors={setBackendErrors}
          isCreateUserFlow={true}
        />
      );
    }

    return null;
  };

  const canGoNext = () => {
    if (createUserFlow.currentStep === 1) {
      return (
        createUserFlow.emailCheckStatus === REQUEST_STATUS.succeeded &&
        !createUserFlow.existingUser
      );
    }

    if (createUserFlow.currentStep === 2) {
      // Can proceed from user details if form is valid
      return true; // Will be validated by Formik
    }

    if (createUserFlow.currentStep === 3) {
      // MUST have at least one account selected to proceed (only for DS accounts)
      return selectedAccounts.length > 0;
    }

    return false;
  };

  const isLoading = createUserStatus === REQUEST_STATUS.loading;
  const isSuccess = createUserStatus === REQUEST_STATUS.succeeded;
  const isFailed = createUserStatus === REQUEST_STATUS.failed;

  // Validation loading states
  const isValidating = validateUserStatus === REQUEST_STATUS.loading;
  const isValidationFailed = validateUserStatus === REQUEST_STATUS.failed;

  // Use pre-filled data if available, otherwise use empty values
  const initialValues = {
    firstName: createUserFlow.preFilledUserData?.firstName || '',
    lastName: createUserFlow.preFilledUserData?.lastName || '',
    phone: createUserFlow.preFilledUserData?.phone || '',
  };

  const stepsData = skipAccountSelection
    ? [
      { label: 'CHECK EMAIL', step: 1 },
      { label: 'USER DETAILS', step: 2 },
      { label: 'ACCOUNT SETTINGS', step: 3 },
    ]
    : [
      { label: 'CHECK EMAIL', step: 1 },
      { label: 'USER DETAILS', step: 2 },
      { label: 'SELECT ACCOUNTS', step: 3 },
      { label: 'ACCOUNT SETTINGS', step: 4 },
    ];

  return (
    <Modal
      open={open}
      title={titles?.createUser || 'Create User'}
      titleChildren={
        <CreateUserStepIndicator
          currentStep={getDisplayStep(createUserFlow.currentStep)}
          totalSteps={getTotalSteps()}
          stepsData={stepsData}
        />
      }
      titleChildrenStyle={classes.titleChildrenStyle}
      handleClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: classes.paper,
      }}
    >
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        enableReinitialize
        onSubmit={handleCreateUser}
      >
        {(formikProps) => {
          const { isValid, isSubmitting, dirty, handleSubmit } = formikProps;

          return (
            <>
              <DialogContent className={classes.content}>
                <div className={classes.stepContent}>
                  {renderStepContent(formikProps)}
                </div>
              </DialogContent>

              <DialogActions className={classes.actions}>
                <div>
                  {createUserFlow.currentStep > 1 && (
                    <Button
                      onClick={handlePrevStep}
                      className={`${classes.actionButton} ${classes.cancelButton}`}
                      variant="outlined"
                    >
                      {btn?.back || 'Back'}
                    </Button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <Button
                    onClick={handleClose}
                    className={`${classes.actionButton} ${classes.cancelButton}`}
                    variant="outlined"
                  >
                    {btn?.cancel || 'Cancel'}
                  </Button>

                  {getDisplayStep(createUserFlow.currentStep) < getTotalSteps() ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleNextStep(formikProps.values)}
                      disabled={!canGoNext() || isValidating}
                      className={classes.actionButton}
                    >
                      {isValidating && createUserFlow.currentStep === 2 ? 'Validating...' : (btn?.next || 'Next')}
                    </Button>
                  ) : (
                    <RoundedButton
                      variant="contained"
                      color="primary"
                      disabled={
                        !isValid ||
                        isLoading ||
                        (!dirty && createUserFlow.currentStep === 2 && !createUserFlow.existingUser) ||
                        !accountSettings ||
                        accountSettings.length === 0
                      }
                      onClick={handleSubmit}
                      isLoading={isLoading}
                      isSuccess={isSuccess}
                      isFailed={isFailed}
                      className={classes.actionButton}
                    >
                      {createUserFlow.existingUser && !isDistributionSponsor
                        ? (btn?.addToAccount || 'Add to Account')
                        : (btn?.createUser || 'Create User')}
                    </RoundedButton>
                  )}
                </div>
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CreateUserModal;
